/**
 * career-credits — the Guthaben surface (route `/credits`, Step 5). Shows the caller's
 * credit balance and a single top-up pack. Buying opens Stripe's HOSTED checkout page —
 * there is NO Stripe.js, no publishable key and no card field here, so the surface carries
 * zero PCI weight (the hosted page is the only payment surface).
 *
 * The WEBHOOK is the single source of truth for a purchase: we NEVER credit client-side and
 * NEVER trust the redirect. On return from a successful checkout we snapshot the pre-checkout
 * balance and poll the wallet until it rises — that rise IS the webhook having landed. A
 * cancelled return is a neutral, no-op state.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { skeletonBalance } from "../lib/skeleton"
import {
  CREDITS_UPDATED_EVENT,
  I18nToken,
  StripeNotConfiguredError,
  fetchWallet,
  startCheckout,
  type WalletView,
} from "../services"
import type { CareerI18n } from "../../i18n"

/** The single credit pack this slice sells (server derives the price from the id). */
const PACK_ID = "credits-1000"
/** sessionStorage key holding the pre-checkout balance, so we know when the webhook credited. */
const SNAPSHOT_KEY = "career:credits:snapshot"
/** Poll cadence while we wait for the webhook to credit; slows down past the calm-note threshold. */
const POLL_FAST_MS = 2_000
const POLL_SLOW_MS = 5_000
const POLL_CALM_AFTER_MS = 60_000

/** The return-state a `?checkout=` param puts the surface into. */
type ReturnMode = "none" | "success" | "cancelled"

function readSnapshot(): number | null {
  try {
    const v = globalThis.sessionStorage?.getItem(SNAPSHOT_KEY)
    if (v === null || v === undefined) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}
function writeSnapshot(balance: number): void {
  try {
    globalThis.sessionStorage?.setItem(SNAPSHOT_KEY, String(balance))
  } catch {
    // storage unavailable — polling falls back to "any load with a balance" (still webhook-truthed).
  }
}
function clearSnapshot(): void {
  try {
    globalThis.sessionStorage?.removeItem(SNAPSHOT_KEY)
  } catch {
    // nothing to clear
  }
}

export class CreditsVm implements ViewModel {
  readonly wallet: Signal<WalletView | null> = signal<WalletView | null>(null)
  readonly loading: Signal<boolean> = signal(true)
  readonly loadError: Signal<boolean> = signal(false)
  /** Checkout redirect in flight (CTA busy). */
  readonly starting: Signal<boolean> = signal(false)
  /** Stripe not configured server-side (503) — calm "try later" state. */
  readonly unavailable: Signal<boolean> = signal(false)
  /** A non-503 checkout failure — retryable inline error. */
  readonly checkoutError: Signal<boolean> = signal(false)
  /** The `?checkout=` return mode. */
  readonly mode: Signal<ReturnMode> = signal<ReturnMode>("none")
  /** Success-return: the webhook has credited (balance rose past the snapshot). */
  readonly credited: Signal<boolean> = signal(false)
  /** Success-return: polling has passed the calm-note threshold. */
  readonly slow: Signal<boolean> = signal(false)

  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** The pre-checkout balance we wait to see exceeded (webhook = truth). */
  private snapshot: number | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private elapsedMs = 0
  private stopped = false

  constructor(i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.wallet, this.loading, this.loadError, this.starting, this.unavailable, this.checkoutError, this.mode, this.credited, this.slow]
  }

  /** Derive the return mode from a query string (seam — tests pass it directly). */
  configureFromSearch(search: string): void {
    const mode = new URLSearchParams(search).get("checkout")
    this.mode.set(mode === "success" ? "success" : mode === "cancelled" ? "cancelled" : "none")
    if (this.mode.value === "cancelled") clearSnapshot()
  }

  attach(): () => void {
    this.configureFromSearch(globalThis.location?.search ?? "")
    void this.load()
    return () => {
      this.stopped = true
      if (this.timer !== null) clearTimeout(this.timer)
    }
  }

  /** Load the wallet; on a success-return, arm the webhook-credited poll. */
  async load(): Promise<void> {
    this.loading.set(true)
    this.loadError.set(false)
    try {
      const wallet = await fetchWallet()
      this.wallet.set(wallet)
      if (this.mode.value === "success") this.armSuccessPoll(wallet.balance)
    } catch {
      this.loadError.set(true)
    } finally {
      this.loading.set(false)
    }
  }

  /** On a successful return, poll the wallet until the balance rises above the pre-checkout snapshot. */
  private armSuccessPoll(currentBalance: number): void {
    this.snapshot = readSnapshot() ?? currentBalance
    // If the balance already exceeds the snapshot (webhook beat us here), we're done.
    if (currentBalance > this.snapshot) {
      this.markCredited()
      return
    }
    this.scheduleNextPoll()
  }

  private scheduleNextPoll(): void {
    if (this.stopped || this.credited.value) return
    const delay = this.elapsedMs >= POLL_CALM_AFTER_MS ? POLL_SLOW_MS : POLL_FAST_MS
    this.timer = setTimeout(() => {
      this.elapsedMs += delay
      if (this.elapsedMs >= POLL_CALM_AFTER_MS) this.slow.set(true)
      void this.pollOnce().then((done) => {
        if (!done) this.scheduleNextPoll()
      })
    }, delay)
  }

  /** One poll tick (seam — tests await it directly). Returns true once credited. */
  async pollOnce(): Promise<boolean> {
    try {
      const wallet = await fetchWallet()
      this.wallet.set(wallet)
      if (this.snapshot !== null && wallet.balance > this.snapshot) {
        this.markCredited()
        return true
      }
    } catch {
      // transient — keep waiting (the webhook is still the source of truth).
    }
    return false
  }

  private markCredited(): void {
    this.credited.set(true)
    clearSnapshot()
    if (typeof globalThis.dispatchEvent === "function") {
      globalThis.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT))
    }
  }

  /** Snapshot the balance, then hand off to Stripe's hosted page. Never credits client-side. */
  async startCheckout(): Promise<void> {
    if (this.starting.value) return
    this.starting.set(true)
    this.unavailable.set(false)
    this.checkoutError.set(false)
    writeSnapshot(this.wallet.value?.balance ?? 0)
    try {
      const { url } = await startCheckout(PACK_ID)
      globalThis.location?.assign(url)
      // Navigation is underway; keep the button busy until the page unloads.
    } catch (e) {
      clearSnapshot()
      this.starting.set(false)
      if (e instanceof StripeNotConfiguredError) this.unavailable.set(true)
      else this.checkoutError.set(true)
    }
  }
}

export class CareerCredits extends KitElement<CreditsVm> {
  protected createViewModel(ctx: KitContext): CreditsVm {
    return new CreditsVm(ctx.inject(I18nToken))
  }

  private balanceUnit(vm: CreditsVm, n: number): string {
    return vm.t("credits.balance.unit", { n: n.toLocaleString("de-DE") })
  }

  /** The return-from-checkout banner (success polling / credited / cancelled). */
  private returnBanner(vm: CreditsVm): TemplateResult | typeof nothing {
    if (vm.mode.value === "cancelled") {
      return html`
        <div class="credits-return credits-return--neutral" role="status">
          <p class="credits-return-title" lang="de">${vm.t("credits.return.cancelled.title")}</p>
          <p class="credits-return-body" lang="de">${vm.t("credits.return.cancelled.body")}</p>
        </div>
      `
    }
    if (vm.mode.value !== "success") return nothing
    if (vm.credited.value) {
      return html`
        <div class="credits-return credits-return--done" role="status">
          <p class="credits-return-title" lang="de">${vm.t("credits.return.success.done")}</p>
        </div>
      `
    }
    return html`
      <div class="credits-return credits-return--pending" role="status" aria-live="polite">
        <span class="credits-spinner" aria-hidden="true"></span>
        <div class="credits-return-text">
          <p class="credits-return-title" lang="de">${vm.t("credits.return.success.title")}</p>
          <p class="credits-return-body" lang="de">${vm.t(vm.slow.value ? "credits.return.success.slow" : "credits.return.success.body")}</p>
        </div>
      </div>
    `
  }

  /** The balance headline — the loading / error / value states. */
  private balanceBlock(vm: CreditsVm): TemplateResult {
    if (vm.loading.value && vm.wallet.value === null) {
      return html`
        <div class="credits-balance" aria-busy="true">
          <span class="credits-balance-label" lang="de">${vm.t("credits.balance.label")}</span>
          ${skeletonBalance()}
        </div>
      `
    }
    if (vm.loadError.value && vm.wallet.value === null) {
      return html`
        <div class="credits-balance-error">
          <p class="surface-error" role="alert" lang="de">${vm.t("credits.balance.error")}</p>
          <button type="button" class="credits-btn credits-btn--ghost" data-act="retry-balance" lang="de">${vm.t("credits.balance.retry")}</button>
        </div>
      `
    }
    const balance = vm.wallet.value?.balance ?? 0
    return html`
      <div class="credits-balance">
        <span class="credits-balance-label" lang="de">${vm.t("credits.balance.label")}</span>
        <span class="credits-balance-value">${balance.toLocaleString("de-DE")}</span>
        <span class="credits-balance-unit" lang="de">${vm.t("credits.chip.label")}</span>
      </div>
    `
  }

  /** The single top-up pack card + CTA. */
  private packCard(vm: CreditsVm): TemplateResult {
    return html`
      <div class="credits-pack">
        <div class="credits-pack-head">
          <span class="credits-pack-name" lang="de">${vm.t("credits.pack.name")}</span>
          <span class="credits-pack-price" lang="de">${vm.t("credits.pack.priceLine")}</span>
        </div>
        <button
          type="button"
          class="credits-btn credits-btn--primary"
          data-act="start-checkout"
          ?disabled=${vm.starting.value}
          aria-busy=${vm.starting.value ? "true" : "false"}
          lang="de"
        >
          ${vm.starting.value ? vm.t("credits.pack.starting") : vm.t("credits.pack.cta")}
        </button>
        <p class="credits-pack-secure" lang="de">${vm.t("credits.pack.secure")}</p>
        ${vm.unavailable.value
          ? html`
              <div class="credits-alert credits-alert--warn" role="alert">
                <p class="credits-alert-title" lang="de">${vm.t("credits.unavailable.title")}</p>
                <p class="credits-alert-body" lang="de">${vm.t("credits.unavailable.body")}</p>
              </div>
            `
          : nothing}
        ${vm.checkoutError.value
          ? html`<p class="surface-error" role="alert" lang="de">${vm.t("credits.checkout.error")}</p>`
          : nothing}
      </div>
    `
  }

  protected render(vm: CreditsVm): TemplateResult {
    return html`
      <section class="surface credits" aria-labelledby="credits-title">
        <h1 id="credits-title" class="surface-title" lang="de">${vm.t("surface.credits.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("credits.lead")}</p>
        ${this.returnBanner(vm)}
        ${this.balanceBlock(vm)}
        ${this.packCard(vm)}
      </section>
    `
  }

  protected override actions(vm: CreditsVm): ActionMap {
    return {
      "start-checkout": () => void vm.startCheckout(),
      "retry-balance": () => void vm.load(),
    }
  }
}
